/**
 * The customer app's themes (spec docs/superpowers/specs/2026-09-27-app-ready-made-themes-design.md).
 *
 * Six choices, as the admin console has them: five ready-made themes — Classic, the app's own plain
 * look, and Ledger, Sentinel, Harbor and Clarity, each with its own colours, shapes and sidebar — and
 * Custom, the person's own: Classic with the theme builder's colour, style and sidebar
 * (components/theme-switcher.tsx, whose storage is unchanged). The builder applies to Custom alone;
 * Light / Dark / System applies to every choice ("Ruling 6").
 *
 * A choice is switched on by `data-app-theme` on <html>, only while a page under the app sidebar is
 * open (components/app-theme/app-theme-scope.tsx). Someone who never chose carries no attribute at all:
 * the page is exactly what it was before themes existed. The play area, the learner portal and the
 * admin console never read this file.
 *
 * This file is data only. The API keeps its own copy of the keys (apps/api/src/modules/users/app-theme.ts)
 * and a parity spec compares the two.
 */

export const APP_THEMES = ['classic', 'ledger', 'sentinel', 'harbor', 'clarity'] as const
export type AppThemeKey = (typeof APP_THEMES)[number]

export const DEFAULT_APP_THEME: AppThemeKey = 'classic'

/** The person's own theme: Classic, with the builder's colour, style and sidebar. */
export const APP_CUSTOM_THEME = 'custom' as const

/** Every value a saved choice may take: a ready-made theme, or Custom. */
export const APP_THEME_CHOICES = [...APP_THEMES, APP_CUSTOM_THEME] as const
export type AppThemeChoice = (typeof APP_THEME_CHOICES)[number]

/** The switch on <html>; app-theme.css matches nothing without it. Absent for someone who never chose. */
export const APP_THEME_ATTRIBUTE = 'data-app-theme'

/**
 * On <html> while the menu previews a theme, naming the look it stands in front of (the saved choice).
 * A preview never moves the menu: while it is on, the sheet sizes the root and sets the menu's face
 * from the saved look (components/app-theme/app-theme.css, "Previews").
 */
export const APP_PREVIEWING_ATTRIBUTE = 'data-app-previewing'

/** Marks the Theme menu itself, so the sheet can keep its face while a preview is on. */
export const APP_THEME_MENU_ATTRIBUTE = 'data-app-theme-menu'

/** Mirrors the user's saved choice for the first paint: `<choice>.<userId>`. */
export const APP_THEME_COOKIE = 'hf-app-theme'

/** The CSS variable next/font sets for Inter (Ledger's typeface), on <html> while a choice is on. */
export const APP_INTER_FONT_VARIABLE = '--font-app-inter'

export interface AppThemeMeta {
  key: AppThemeKey
  label: string
  /** One line, plain words: what the theme is for. */
  description: string
  /** Shown as a hint only; never changes the user's own light/dark choice. */
  bestIn?: 'light' | 'dark'
  /** Canvas, accent, second chart colour — for the menu. */
  swatches: [string, string, string]
  /** `today`: whatever the app uses today (Classic changes nothing). */
  font: 'today' | 'geist' | 'inter'
  /** The sidebar the theme brings — the console's: Harbor floats, the rest sit docked. */
  sidebar: 'sidebar' | 'floating'
}

export const APP_THEME_META: Record<AppThemeKey, AppThemeMeta> = {
  classic: {
    key: 'classic',
    label: 'Classic',
    description: 'The app’s own look: soft corners, white panels and its standard colours.',
    swatches: ['oklch(1 0 0)', 'oklch(0.205 0 0)', 'oklch(0.65 0.25 250)'],
    font: 'today',
    sidebar: 'sidebar',
  },
  ledger: {
    key: 'ledger',
    label: 'Ledger',
    description: 'Boxy and high-contrast: a grey canvas, white panels, crisp borders and square corners.',
    swatches: ['oklch(0.967 0 0)', 'oklch(0.47 0.2 262)', 'oklch(0.6 0.12 190)'],
    font: 'inter',
    sidebar: 'sidebar',
  },
  sentinel: {
    key: 'sentinel',
    label: 'Sentinel',
    description: 'An operations console: deep navy, a cyan signal colour and a dark sidebar.',
    bestIn: 'dark',
    swatches: ['oklch(0.15 0.02 255)', 'oklch(0.8 0.13 190)', 'oklch(0.8 0.14 75)'],
    font: 'geist',
    sidebar: 'sidebar',
  },
  harbor: {
    key: 'harbor',
    label: 'Harbor',
    description: 'Calm and roomy: round corners, a floating sidebar and a sea-green accent.',
    swatches: ['oklch(0.975 0.008 170)', 'oklch(0.53 0.1 172)', 'oklch(0.65 0.14 35)'],
    font: 'geist',
    sidebar: 'floating',
  },
  clarity: {
    key: 'clarity',
    label: 'Clarity',
    description: 'Accessibility first: the strongest contrast, a thick focus ring and slightly larger text.',
    swatches: ['oklch(1 0 0)', 'oklch(0.42 0.19 264)', '#c78200'],
    font: 'geist',
    sidebar: 'sidebar',
  },
}

/** The Custom option in the menu — the console's words. */
export const APP_CUSTOM_META = {
  key: APP_CUSTOM_THEME,
  label: 'Custom',
  description: 'Build your own on Classic: a colour, a corner style and how the sidebar sits.',
} as const

export const isAppThemeKey = (value: unknown): value is AppThemeKey =>
  typeof value === 'string' && (APP_THEMES as readonly string[]).includes(value)

export const isAppThemeChoice = (value: unknown): value is AppThemeChoice =>
  typeof value === 'string' && (APP_THEME_CHOICES as readonly string[]).includes(value)

/** A choice's name, as the menu shows it. */
export function appThemeLabel(choice: AppThemeChoice): string {
  return choice === APP_CUSTOM_THEME ? APP_CUSTOM_META.label : APP_THEME_META[choice].label
}
